import type {ReactElement} from 'react';
import React from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {useUpdateEffect} from 'ahooks';
import {ItemSizeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-size-option';
import {TextSlideSizeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-slide-size-option';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {TranscriptFontSizeOption} from '@Components/poster-editor/components/transcript-font-size-option/transcript-font-size-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';

interface PosterEditorItemFontSizePanelProps {
  panelId: string;
}

export default function PosterEditorItemFontSizePanel({...props}: PosterEditorItemFontSizePanelProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const onClose = useDefaultPanelClose(props.panelId);

  useUpdateEffect((): void => {
    onClose();
  }, [activeItem]);

  useDidUpdateEffect((): void => {
    if (activeItem?.isSlideshow()) {
      onClose();
    }
  }, [activeSlideUID]);

  if (!activeItem?.hasEditableText()) {
    return null;
  }

  if (!activeItem.isTranscript()) {
    if (!('textStyles' in activeItem)) {
      return null;
    }
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_size')} />;
  };

  const getSizeOption = (): ReactElement | null => {
    if (activeItem.isSlideshow() && activeSlide?.isTextSlide()) {
      return <TextSlideSizeOption />;
    }

    if (activeItem.isTranscript()) {
      return <TranscriptFontSizeOption />;
    }

    return <ItemSizeOption />;
  };

  const getContent = (): ReactElement => {
    return <div className="spacing-p-t-1 spacing-p-r-4 spacing-p-l-4 spacing-p-b-4">{getSizeOption()}</div>;
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
}
